// src/components/AdminRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import axios from 'axios';
import AccessDenied from './Admin/AccessDenied';
import { Spinner } from 'react-bootstrap';

const API_BASE_URI = process.env.REACT_APP_API_BASE_URI || 'http://localhost:5001/api';

const AdminRoute = ({ children }) => {
  const { user, userProfile, loading: authLoading } = useUserAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        console.log('No user detected, redirecting to login');
        setCheckingAdmin(false);
        return;
      }

      // First check if we have the role in userProfile
      if (userProfile && userProfile.role === 'admin') {
        console.log('Admin status confirmed via userProfile');
        setIsAdmin(true);
        setCheckingAdmin(false);
        return;
      }

      try {
        console.log('Verifying admin access via API');
        const token = await user.getIdToken(true); // Force refresh token

        const response = await axios.get(`${API_BASE_URI}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 200) {
          console.log('Admin API access successful');
          setIsAdmin(true);
        } else {
          console.warn('Unexpected response from admin API:', response.status);
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Admin access verification failed:', err);
        setError(err.message || 'Failed to verify admin permissions');
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, userProfile, authLoading]);

  // Show loading state while checking auth or admin status
  if (authLoading || checkingAdmin) {
    return (
        <div className="admin-loading-screen">
          <div className="admin-loading-content">
            <Spinner animation="border" role="status" className="admin-spinner">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="admin-loading-text">Verifying admin access...</p>
          </div>
        </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Show access denied page if authenticated but not admin
  if (!isAdmin) {
    return <AccessDenied error={error} />;
  }

  // Show admin content if all checks pass
  return children;
};

export default AdminRoute;